"use client";
import { CheckCircle2, GripVertical, Minimize2, X } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';

export interface ChecklistItem {
  id: string;
  label: string;
  done: boolean;
}

interface FloatingChecklistProps {
  title?: string;
  items: ChecklistItem[];
  hideWhenComplete?: boolean;
  onItemClick?: (id: string) => void;
}

export default function FloatingChecklist({ title = 'Checklist', items, hideWhenComplete = true, onItemClick }: FloatingChecklistProps) {
  const [position, setPosition] = useState<{ x: number; y: number }>({ x: 24, y: 24 });
  const [isDragging, setIsDragging] = useState(false);
  const [offset, setOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [isMinimized, setIsMinimized] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const allDone = useMemo(() => items.length > 0 && items.every(i => i.done), [items]);

  useEffect(() => {
    if (hideWhenComplete && allDone) {
      // Hide by minimizing when all done
       
      setIsMinimized(true);
    }
  }, [allDone, hideWhenComplete]);

  useEffect(() => {
    const handlePointerMove = (e: PointerEvent) => {
      if (!isDragging || !containerRef.current) return;
      const el = containerRef.current;
      const width = el.offsetWidth;
      const height = el.offsetHeight;
      const maxX = Math.max(0, window.innerWidth - width - 8);
      const maxY = Math.max(0, window.innerHeight - height - 8);
      const nextX = Math.min(Math.max(8, e.clientX - offset.x), maxX);
      const nextY = Math.min(Math.max(8, e.clientY - offset.y), maxY);
      setPosition({ x: nextX, y: nextY });
    };
    const handlePointerUp = () => setIsDragging(false);
    if (isDragging) {
      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', handlePointerUp);
    }
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [isDragging, offset.x, offset.y]);

  // Center by default on first mount
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    // Allow layout to settle
    const center = () => {
      const width = el.offsetWidth || 320;
      const height = el.offsetHeight || 200;
      const x = Math.max(8, Math.round((window.innerWidth - width) / 2));
      const y = Math.max(8, Math.round((window.innerHeight - height) / 2));
      setPosition({ x, y });
    };
    // Use rAF to ensure sizes are measured after render
    const id = requestAnimationFrame(center);
    return () => cancelAnimationFrame(id);
  }, []);

  const onPointerDown = (e: React.PointerEvent) => {
    if (!containerRef.current) return;
    setIsDragging(true);
    const rect = containerRef.current.getBoundingClientRect();
    setOffset({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  if (hideWhenComplete && allDone && isMinimized) return null;

  return (
    <div
      ref={containerRef}
      className="fixed z-40 max-w-sm w-[360px] shadow-2xl border border-sky-200/60 bg-white/98 backdrop-blur-xl rounded-2xl select-none overflow-hidden"
      style={{ left: position.x, top: position.y, cursor: isDragging ? 'grabbing' : 'default' }}
    >
      <div
        className="flex items-center justify-between px-5 py-3.5 bg-gradient-to-r from-sky-50/80 via-white to-blue-50/80 border-b border-sky-200/40 backdrop-blur-sm"
        onPointerDown={onPointerDown}
      >
        <div className="flex items-center gap-3">
          <GripVertical className="h-4 w-4 text-sky-400/70 cursor-grab active:cursor-grabbing" />
          <div className="flex items-center gap-2.5">
            <span className="font-semibold text-gray-800 text-sm tracking-tight">{title}</span>
            <span className="px-2.5 py-1 text-xs font-semibold bg-sky-500/10 text-sky-700 rounded-full border border-sky-200/50">
              {items.filter(i => i.done).length}/{items.length}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-0.5">
          <button
            type="button"
            className="p-1.5 rounded-lg hover:bg-sky-100/60 text-sky-500/80 hover:text-sky-600 transition-all"
            onClick={() => setIsMinimized(v => !v)}
            aria-label={isMinimized ? 'Expand' : 'Minimize'}
          >
            <Minimize2 className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            className="p-1.5 rounded-lg hover:bg-red-50/60 text-red-400/70 hover:text-red-500 transition-all"
            onClick={() => setIsMinimized(true)}
            aria-label="Close"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <div className="p-4 max-h-[420px] overflow-y-auto custom-scrollbar bg-gradient-to-b from-white via-white to-sky-50/20">
          <ul className="space-y-1.5">
            {items.map((item, index) => (
              <li key={`${item.id}-${index}`}>
                <button
                  type="button"
                  onClick={() => onItemClick && onItemClick(item.id)}
                  className={`w-full flex items-center gap-3 text-left rounded-xl px-3.5 py-2.5 transition-all duration-200 group border ${
                    item.done 
                      ? 'bg-green-50/40 hover:bg-green-50/60 border-green-200/40 hover:border-green-300/50' 
                      : 'bg-amber-50/40 hover:bg-amber-50/60 border-amber-200/40 hover:border-amber-300/50'
                  }`}
                >
                  <div className={`flex-shrink-0 transition-all ${
                    item.done 
                      ? 'text-green-500' 
                      : 'text-amber-500/70 group-hover:text-amber-500'
                  }`}>
                    <CheckCircle2 className={`h-4.5 w-4.5 ${item.done ? 'fill-green-500 text-white' : ''}`} />
                  </div>
                  <span className={`text-sm leading-snug flex-1 ${
                    item.done 
                      ? 'text-gray-500 line-through decoration-gray-300' 
                      : 'text-gray-700 font-medium'
                  }`}>
                    {item.label}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          {allDone && (
            <div className="mt-4 pt-4 border-t border-sky-100/50">
              <div className="flex items-center gap-2.5 px-3.5 py-2.5 bg-gradient-to-r from-green-50/60 to-emerald-50/60 rounded-xl border border-green-200/50">
                <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
                <span className="text-xs font-semibold text-green-700">All items completed</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


